import { v4 as uuid } from "uuid";
import {
  swimlaneModelCreator,
  swimlaneModelCreatorAppJSON
} from "./swimlane.model";
import type {
  ConditionGroupMap,
  ConditionMap
} from "../components/RulesUI/Constraints/constraints.types";
import { getRulesModel } from "./rules.model";
import type { ZodRawProcess } from "@certa/queries/schemas/studio/deployableJSON.schema";
import type {
  Field,
  Process,
  ProcessProps,
  RuleMap,
  Step
} from "@certa/studio-core";
import { RuleType, getSlugifiedTag } from "@certa/studio-core";
import { migrations } from "../constants/migration.constants";
import { createEventRule, createStateRule } from "./eca.model";
import type {
  EventRule,
  StateRule
} from "@certa/studio-core/src/types/eca.types";
import type { ProcessRawAppJSON } from "@certa/queries/schemas/studio/appProcessJSON.schema";
import { removeStudioParams } from "../utils/general";

/**
 * TODO:
 * - Handle unhandled props
 * - Handle Data inheritance migration from v1 to v2
 */

const constraintRuleTypeMapping: Record<string, RuleType> = {
  accessGroupConstraints: RuleType.WorkflowAccess
};

const constraintRawPropMapping = {
  accessGroupConstraints: "access_group_constraints"
} as const;

const processConstraintKeys = ["accessGroupConstraints"] as const;

/**
 * @description
 * Omit all non-process props from the raw process so that it doesn't included in the processProps because of ...restProcessProps.
 * restProcessProps should have only params that are yet to be supported by studio.
 * @param rawProcess - The raw process to create the process props from; should not include event_rules, states, or stepgroups.
 * @param processId - The ID of the process
 * @returns The process props
 */
type ProcessPropModelCreatorRawProcess = Omit<
  ZodRawProcess | ProcessRawAppJSON,
  "event_rules" | "states" | "stepgroups"
>;

const processPropModelCreator = (
  rawProcess: ProcessPropModelCreatorRawProcess,
  { processId }: { processId: string }
) => {
  const {
    kind: {
      available_statuses: availableStatuses,
      default_status: defaultStatus,
      name,
      visible_to_static_groups: visibleToStaticGroups,
      tag,
      object_types: objectTypes,
      permissions: {
        dynamic_groups: permissionsDynamicGroups,
        static_groups: permissionsStaticGroups,
        ...restKindPermissionsProps
      },
      ...restKindProps
    },
    creator_permissions: creatorPermissions,
    default_step_tags: defaultStepTags,
    default_steps_for_groups: defaultStepTagForGroups,
    dynamic_group_names: dynamicGroupNames,
    dynamic_group_names_with_perm: dynamicGroupNamesWithPerm,
    mentions_template: mentionsTemplate,
    extra_fields_json: extraFieldsJSON,
    on_delete_integration_field_tags: onDeleteIntegrationFieldTags,
    auto_integration_call_field_tags: autoIntegrationCallFieldTags,
    static_groups: staticGroups,
    node_order: nodeOrder,
    pdf_configurations: pdfConfigurations,
    related_types: relatedTypes,
    bulk_actions: bulkActionsRaw,
    default_threads: defaultThreads,
    model_req_fields: modelReqFields,
    content_type: contentType,
    child_mappings: childMappings,
    on_create_workflow: onCreateWorkflow,

    name: nameRaw,
    ...restProcessProps
  } = rawProcess;
  const restProcessPropsWithoutStudioParams =
    removeStudioParams(restProcessProps);

  const defaultLastMigrationId = migrations[migrations.length - 1].id;
  const processProps: ProcessProps = {
    ...restProcessPropsWithoutStudioParams,
    id: processId,
    kind: {
      availableStatuses,
      defaultStatus,
      name: name, // not to be changed later, it's used to derive the kind tag
      visibleToStaticGroups,
      tag: tag || getSlugifiedTag(name, "-"), // readonly
      objectTypes,
      permissions: {
        dynamicGroups: permissionsDynamicGroups,
        staticGroups: permissionsStaticGroups,
        ...restKindPermissionsProps
      },
      ...restKindProps
    },
    creatorPermissions,
    defaultStepTags,
    defaultStepTagForGroups,
    dynamicGroupNames,
    dynamicGroupNamesWithPerm,
    mentionsTemplate,
    lcData: extraFieldsJSON?.map(rawLCData => {
      const {
        display_type: displayType,
        label,
        format,
        ...restLCDataProps
      } = rawLCData;
      return {
        ...restLCDataProps,
        displayType,
        label,
        format
      };
    }),
    onDeleteIntegrationFieldTags,
    autoIntegrationCallFieldTags,
    staticGroups,
    nodeOrder,
    pdfConfigurations,
    relatedTypes,
    bulkActionsRaw,
    defaultThreads,
    modelReqFields,
    contentType,
    childMappings,
    onCreateWorkflow,
    swimlaneSequence: [],
    eventRuleSequence: [],
    stateRuleSequence: [],
    __createdAt:
      "__studio_created_at" in rawProcess &&
      rawProcess.__studio_created_at &&
      typeof rawProcess.__studio_created_at === "number"
        ? rawProcess.__studio_created_at
        : Date.now(),
    __modifiedAt:
      "__studio_modified_at" in rawProcess &&
      rawProcess.__studio_modified_at &&
      typeof rawProcess.__studio_modified_at === "number"
        ? rawProcess.__studio_modified_at
        : Date.now(),
    __branchTimestamp:
      "__studio_branch_timestamp" in rawProcess &&
      rawProcess.__studio_branch_timestamp &&
      typeof rawProcess.__studio_branch_timestamp === "number"
        ? rawProcess.__studio_branch_timestamp
        : Date.now(),
    __saveId:
      "__studio_save_id" in rawProcess &&
      rawProcess.__studio_save_id &&
      typeof rawProcess.__studio_save_id === "string"
        ? rawProcess.__studio_save_id
        : uuid(),
    __lastMigration:
      "__studio_last_migration" in rawProcess &&
      rawProcess.__studio_last_migration &&
      typeof rawProcess.__studio_last_migration === "string"
        ? rawProcess.__studio_last_migration
        : defaultLastMigrationId
  };

  let aggregatedRuleMap: RuleMap = {};
  let aggregatedConditionGroupMap: ConditionGroupMap = {};
  let aggregatedConditionMap: ConditionMap = {};

  // Now we parse all constraints and populate them accordingly.
  processConstraintKeys.forEach(constraintKey => {
    // as we are doing ...restProcessProps, we need to delete the constraint from the processProps
    // that we have added to the processProps to avoid duplication
    if (constraintRawPropMapping[constraintKey] in processProps) {
      delete processProps[constraintRawPropMapping[constraintKey]];
    }
    if (!rawProcess[constraintRawPropMapping[constraintKey]]) {
      return;
    }
    const { ruleMap, conditionGroupMap, conditionMap } = getRulesModel(
      rawProcess[constraintRawPropMapping[constraintKey]],
      constraintRuleTypeMapping[constraintKey],
      { processId, targetFieldTag: processProps.kind.tag }
    );

    aggregatedRuleMap = { ...aggregatedRuleMap, ...ruleMap };
    aggregatedConditionGroupMap = {
      ...aggregatedConditionGroupMap,
      ...conditionGroupMap
    };

    aggregatedConditionMap = { ...aggregatedConditionMap, ...conditionMap };

    processProps[constraintKey] = Object.keys(ruleMap);
  });

  return {
    processProps,
    aggregatedRuleMap,
    aggregatedConditionGroupMap,
    aggregatedConditionMap
  };
};

export const processModelCreator = (
  rawProcess: ZodRawProcess,
  { processId }: { processId: string }
) => {
  const {
    event_rules: eventRulesParam,
    states: statesParam,
    stepgroups: stepgroupsParam,
    ...rawProcessProps
  } = rawProcess;
  const {
    processProps,
    aggregatedRuleMap: aggregatedProcessRuleMap,
    aggregatedConditionGroupMap: aggregatedProcessConditionGroupMap,
    aggregatedConditionMap: aggregatedProcessConditionMap
  } = processPropModelCreator(rawProcessProps, { processId });

  let eventRules: Record<string, EventRule> = {};
  let stateRules: Record<string, StateRule> = {};

  // Only add eventRules if event_rules are present in rawProcess
  if (eventRulesParam && eventRulesParam.length > 0) {
    eventRules = eventRulesParam.reduce(
      (acc: Record<string, EventRule>, eventRuleRaw) => {
        const eventRule = createEventRule(eventRuleRaw);
        acc[eventRule.__studioId] = eventRule;
        processProps.eventRuleSequence.push(eventRule.__studioId); // Store ID in sequence array
        return acc;
      },
      {}
    );
  }

  if (statesParam && statesParam.length > 0) {
    stateRules = statesParam.reduce(
      (acc: Record<string, StateRule>, stateRuleRaw) => {
        const stateRule = createStateRule(stateRuleRaw);
        acc[stateRule.__studioId] = stateRule;
        processProps.stateRuleSequence.push(stateRule.__studioId); // Store ID in sequence array
        return acc;
      },
      {}
    );
  }

  let aggregatedRuleMap: RuleMap = { ...aggregatedProcessRuleMap };
  let aggregatedConditionGroupMap: ConditionGroupMap = {
    ...aggregatedProcessConditionGroupMap
  };
  let aggregatedConditionMap: ConditionMap = {
    ...aggregatedProcessConditionMap
  };

  /**
   * Parsing Swimlanes
   */

  const swimlanesRaw = Object.entries(stepgroupsParam)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(swimlaneKV => swimlaneKV[1]);

  let aggregatedFieldsMap: Record<string, Field> = {};
  let aggregatedStepsMap: Record<string, Step> = {};
  let aggregatedChildMappings: Process["process"]["childMappings"] = {};

  const swimlanesArray = swimlanesRaw.map(rawSwimlane => {
    const {
      swimlane,
      stepsMap,
      fieldsMap,
      conditionGroupMap,
      conditionMap,
      ruleMap,
      childMappings
    } = swimlaneModelCreator(rawSwimlane, { processId });
    aggregatedFieldsMap = { ...aggregatedFieldsMap, ...fieldsMap };
    aggregatedStepsMap = { ...aggregatedStepsMap, ...stepsMap };

    aggregatedRuleMap = { ...aggregatedRuleMap, ...ruleMap };
    aggregatedConditionGroupMap = {
      ...aggregatedConditionGroupMap,
      ...conditionGroupMap
    };
    aggregatedConditionMap = { ...aggregatedConditionMap, ...conditionMap };
    aggregatedChildMappings = {
      ...aggregatedChildMappings,
      ...childMappings
    };
    return swimlane;
  });

  const swimlaneMap = swimlanesArray.reduce(
    (aggregator, swimlane) => ({ ...aggregator, [swimlane.id]: swimlane }),
    {}
  );
  const swimlaneSequence = swimlanesArray.map(swimlane => swimlane.id);

  const isChildMappingPresent =
    Object.keys(processProps.childMappings || {}).length > 0;
  const isAggregatedChildMappingPresent =
    Object.keys(aggregatedChildMappings || {}).length > 0;

  const process: Process = {
    process: {
      ...processProps,
      swimlaneSequence,
      childMappings:
        isChildMappingPresent || isAggregatedChildMappingPresent
          ? {
              ...(processProps.childMappings ?? {}),
              ...(aggregatedChildMappings ?? {})
            }
          : undefined
      // bulkActions: bulkActionsUUIDs
    },
    swimlanes: swimlaneMap,
    steps: aggregatedStepsMap,
    fields: aggregatedFieldsMap,
    conditions: aggregatedConditionMap,
    conditionGroups: aggregatedConditionGroupMap,
    rules: aggregatedRuleMap,
    bulkActions: {},
    eventRules,
    stateRules
  };

  return process;
};

export const processModelCreatorFromAppJSON = (
  rawProcess: ProcessRawAppJSON,
  { processId }: { processId: string }
) => {
  const {
    event_rules: eventRulesParam,
    states: statesParam,
    stepgroups: stepgroupsParam,
    ...rawProcessProps
  } = rawProcess;
  const { processProps } = processPropModelCreator(rawProcessProps, {
    processId
  });

  let eventRules: Record<string, EventRule> = {};
  let stateRules: Record<string, StateRule> = {};

  // Only add eventRules if event_rules are present in rawProcess
  if (eventRulesParam && eventRulesParam.length > 0) {
    eventRules = eventRulesParam.reduce(
      (acc: Record<string, EventRule>, eventRuleRaw) => {
        const eventRule = createEventRule(eventRuleRaw);
        acc[eventRule.__studioId] = eventRule;
        processProps.eventRuleSequence.push(eventRule.__studioId); // Store ID in sequence array
        return acc;
      },
      {}
    );
  }

  if (statesParam && statesParam.length > 0) {
    stateRules = statesParam.reduce(
      (acc: Record<string, StateRule>, stateRuleRaw) => {
        const stateRule = createStateRule(stateRuleRaw);
        acc[stateRule.__studioId] = stateRule;
        processProps.stateRuleSequence.push(stateRule.__studioId); // Store ID in sequence array
        return acc;
      },
      {}
    );
  }

  /**
   * Parsing Swimlanes
   */

  let aggregatedFieldsMap: Record<string, Field> = {};
  let aggregatedStepsMap: Record<string, Step> = {};
  let aggregatedChildMappings: Process["process"]["childMappings"] = {};

  const swimlanesArray = stepgroupsParam.map(rawSwimlane => {
    const { swimlane, stepsMap, fieldsMap, childMappings } =
      swimlaneModelCreatorAppJSON(rawSwimlane, { processId });
    aggregatedFieldsMap = { ...aggregatedFieldsMap, ...fieldsMap };
    aggregatedStepsMap = { ...aggregatedStepsMap, ...stepsMap };

    aggregatedChildMappings = {
      ...aggregatedChildMappings,
      ...childMappings
    };
    return swimlane;
  });

  const swimlaneMap = swimlanesArray.reduce(
    (aggregator, swimlane) => ({ ...aggregator, [swimlane.id]: swimlane }),
    {}
  );
  const swimlaneSequence = swimlanesArray.map(swimlane => swimlane.id);

  const isChildMappingPresent =
    Object.keys(processProps.childMappings || {}).length > 0;
  const isAggregatedChildMappingPresent =
    Object.keys(aggregatedChildMappings || {}).length > 0;

  const process: Process = {
    process: {
      ...processProps,
      swimlaneSequence,
      childMappings:
        isChildMappingPresent || isAggregatedChildMappingPresent
          ? {
              ...(processProps.childMappings ?? {}),
              ...(aggregatedChildMappings ?? {})
            }
          : undefined
      // bulkActions: bulkActionsUUIDs
    },
    swimlanes: swimlaneMap,
    steps: aggregatedStepsMap,
    fields: aggregatedFieldsMap,
    conditions: {},
    conditionGroups: {},
    rules: {},
    bulkActions: {},
    eventRules,
    stateRules
  };

  return process;
};
